import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, MapPin, Navigation } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import Stepper from 'react-stepper-horizontal';
import { onboardAmbulanceVendor, updateAmbulanceVendor } from '../../../services/SalesExecutive/vendorOnboardingService';
import useAuth from '../../../hooks/useAuth';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { stateCityList } from '../../../data/stateCityList';

// Ambulance fields
const ambulanceFields = [
  { name: 'agencyName', label: 'Agency Name' },
  { name: 'gstNumber', label: 'GST Number' },
  { name: 'panNumber', label: 'PAN Number' },
  { name: 'ownerName', label: 'Owner Name' },
  { name: 'registrationNumber', label: 'Registration Number' },
  { name: 'completeAddress', label: 'Complete Address' },
  { name: 'nearbyLandmark', label: 'Nearby Landmark' },
  { name: 'phoneNumber', label: 'Phone Number' },
  { name: 'state', label: 'State' },
  { name: 'city', label: 'City' },
  { name: 'pincode', label: 'Pin Code' },
  { name: 'email', label: 'Email' },
  { name: 'website', label: 'Website' },
  { name: 'numOfAmbulances', label: 'Number of Ambulances' },
  { name: 'driverKYC', label: 'Driver KYC' },
  { name: 'driverTrained', label: 'Driver Trained' },
  { name: 'ambulanceTypes', label: 'Ambulance Types' },
  { name: 'gpsTrackingAvailable', label: 'GPS Tracking Available' },
  { name: 'ambulanceEquipment', label: 'Ambulance Equipment' },
  { name: 'driverLicense', label: 'Driver License' },
  { name: 'languageProficiency', label: 'Language Proficiency' },
  { name: 'deliveryOperationalAreas', label: 'Delivery Operational Areas' },
  { name: 'distanceLimitations', label: 'Distance Limitations' },
  { name: 'is24x7Operational', label: '24x7 Operational' },
  { name: 'isAllDaysWorking', label: 'All Days Working' },
  { name: 'acceptsOnlinePayment', label: 'Accepts Online Payment' },
  { name: 'agencyPhotos', label: 'Agency Photos' },
  { name: 'licenseFiles', label: 'License Files' },
  { name: 'registrationCertificateFiles', label: 'Registration Certificate Files' },
  { name: 'googleMapsLocation', label: 'Google Maps Location' },
];

const stepSections = [
  {
    title: 'Basic Information',
    fields: [
      'agencyName', 'ownerName', 'phoneNumber', 'email', 'website', 'registrationNumber',
      'numOfAmbulances', 'driverKYC', 'driverTrained', 'driverLicense',
    ],
  },
  {
    title: 'Business & Services',
    fields: [
      'gstNumber', 'panNumber', 'licenseFiles', 'registrationCertificateFiles', 'agencyPhotos',
      'ambulanceTypes', 'gpsTrackingAvailable', 'ambulanceEquipment', 'languageProficiency',
      'acceptsOnlinePayment', 'deliveryOperationalAreas', 'distanceLimitations',
    ],
  },
  {
    title: 'Location & Operations',
    fields: [
      'completeAddress', 'nearbyLandmark', 'state', 'city', 'pincode', 'googleMapsLocation',
      'is24x7Operational', 'isAllDaysWorking',
    ],
  },
];

const getFieldsForStep = (step: number) => {
  const section = stepSections[step];
  return ambulanceFields.filter(f => section.fields.includes(f.name));
};

// MultiValueInput component for adding/removing multiple values
function MultiValueInput({ label, name, values, onChange }: {
  label: string;
  name: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [input, setInput] = React.useState('');
  const handleAdd = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput('');
    }
  };
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };
  const handleRemove = (val: string) => {
    onChange(values.filter(v => v !== val));
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          name={name}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2"
          placeholder={`Add ${label.toLowerCase()}`}
        />
        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-2 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {values.map(val => (
          <span key={val} className="flex items-center bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full text-sm">
            {val}
            <button
              type="button"
              onClick={() => handleRemove(val)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ToggleSwitch component for boolean fields
function ToggleSwitch({ label, name, checked, onChange }: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 min-w-[140px]">{label}</label>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-primary-600' : 'bg-gray-400 dark:bg-gray-700'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`}
        />
      </button>
      <span className="ml-2 text-xs text-gray-500">{checked ? 'Yes' : 'No'}</span>
    </div>
  );
}

// FileUploadComponent for file uploads
function FileUploadComponent({ fieldName, label, files, onFilesChange }: {
  fieldName: string;
  label: string;
  files: Array<{ id: string; name: string; file: File }>;
  onFilesChange: (files: Array<{ id: string; name: string; file: File }>) => void;
}) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles).map(file => ({
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        name: file.name.replace(/\.[^/.]+$/, ""),
        file: file
      }));
      onFilesChange([...files, ...newFiles]);
    }
  };
  const handleNameChange = (id: string, newName: string) => {
    onFilesChange(files.map(f => f.id === id ? { ...f, name: newName } : f));
  };
  const removeFile = (id: string) => {
    onFilesChange(files.filter(f => f.id !== id));
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
        />
      </div>
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Uploaded {label}:
          </div>
          {files.map((fileData) => (
            <div key={fileData.id} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <input
                  type="text"
                  value={fileData.name}
                  onChange={(e) => handleNameChange(fileData.id, e.target.value)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="Enter file name"
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {fileData.file.name} ({(fileData.file.size / 1024).toFixed(1)} KB)
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(fileData.id)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// MapComponent for Google Maps location (uses OpenStreetMap for now)
function MapComponent({ coordinates, onGetLocation, isGettingLocation }: {
  coordinates: { lat: number; lng: number } | null;
  onGetLocation: () => void;
  isGettingLocation: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onGetLocation}
          disabled={isGettingLocation}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Navigation className="w-4 h-4" />
          {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
        </button>
      </div>
      {coordinates && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Map Preview</span>
            </div>
          </div>
          <div className="w-full h-48 bg-gray-200 dark:bg-gray-600">
            <iframe
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${coordinates.lng - 0.01},${coordinates.lat - 0.01},${coordinates.lng + 0.01},${coordinates.lat + 0.01}&layer=mapnik&marker=${coordinates.lat},${coordinates.lng}`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              title="Location Map"
              loading="lazy"
            />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const parseFilesSafely = (value: any): Array<{ name: string; url: string }> => {
  if (!value) return [];
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseFilesSafely(parsed);
      } catch (e) {
        // Fallback
      }
    }
    const name = value.split('/').pop()?.split('?')[0] || 'file';
    return [{ name, url: value }];
  }
  
  if (Array.isArray(value)) {
    return value.map((item: any) => {
      if (typeof item === 'string') {
        const name = item.split('/').pop()?.split('?')[0] || 'file';
        return { name, url: item };
      }
      if (item && typeof item === 'object') {
        const url = item.url || item.uri || '';
        const name = item.name || url.split('/').pop()?.split('?')[0] || 'file';
        return { name, url };
      }
      return { name: 'file', url: String(item) };
    }).filter(item => item.url);
  }
  
  if (typeof value === 'object') {
    const url = value.url || value.uri || '';
    if (url) {
      const name = value.name || url.split('/').pop()?.split('?')[0] || 'file';
      return [{ name, url }];
    }
  }
  
  return [];
};

const AmbulanceVendorForm: React.FC = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<{ [key: string]: any }>({});
  const [uploadedFiles, setUploadedFiles] = useState<{
    [key: string]: Array<{ id: string; name: string; file: File }>
  }>({
    licenseFiles: [],
    registrationCertificateFiles: [],
    agencyPhotos: [],
  });
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { user } = useAuth();
  const { vendorId: urlVendorId } = useParams<{ vendorId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedState, setSelectedState] = useState<string>('');
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  // Check if this is edit mode
  const vendorData = location.state?.vendorData;
  const vendorId = urlVendorId || vendorData?.vendor?.vendorId || vendorData?.fullProfile?.vendorId;
  const isEditMode = !!vendorId;

  // Populate form data when in edit mode
  useEffect(() => {
    if (isEditMode && vendorData?.fullProfile) {
      const profile = vendorData.fullProfile;
      setForm({
        agencyName: profile.agencyName || '',
        gstNumber: profile.gstNumber || '',
        panNumber: profile.panNumber || '',
        ownerName: profile.ownerName || '',
        completeAddress: profile.address || '', // API uses 'address'
        nearbyLandmark: profile.landmark || '', // API uses 'landmark'
        phoneNumber: profile.contactNumber || '', // API uses 'contactNumber'
        state: profile.state || '',
        city: profile.city || '',
        pincode: profile.pinCode || '', // API uses 'pinCode'
        email: profile.email || '',
        website: profile.website || '',
        languageProficiency: profile.languageProficiency || [],
        deliveryOperationalAreas: profile.operationalAreas || [], // API uses 'operationalAreas'
        distanceLimitations: profile.distanceLimit || '', // API uses 'distanceLimit'
        is24x7Operational: profile.is24x7Available || false, // API uses 'is24x7Available'
        isAllDaysWorking: profile.isAllDaysWorking || false,
        acceptsOnlinePayment: profile.isOnlinePaymentAvailable || false, // API uses 'isOnlinePaymentAvailable'
        googleMapsLocation: profile.preciseLocation || '', // API uses 'preciseLocation'
        numOfAmbulances: profile.numOfAmbulances || '',
        driverKYC: profile.driverKYC || false,
        driverTrained: profile.driverTrained || false,
        ambulanceTypes: profile.ambulanceTypes || [],
        gpsTrackingAvailable: profile.gpsTrackingAvailable || false,
        ambulanceEquipment: profile.ambulanceEquipment || [],
        driverLicense: profile.driverLicense || '',
        registrationNumber: profile.registrationNumber || '',
      });
      
      // Set coordinates if location exists
      if (profile.preciseLocation) {
        const [lat, lng] = profile.preciseLocation.split(',').map((coord: string) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          setCoordinates({ lat, lng });
        }
      }

      // Set uploaded files for edit mode
      const licFiles = parseFilesSafely(profile.trainingCertifications);
      if (licFiles.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          licenseFiles: licFiles.map((cert, index) => ({
            id: `cert-${index}`,
            name: cert.name,
            file: new File([], cert.name) // Create a dummy file for display
          }))
        }));
      }

      const agencyPhotos = parseFilesSafely(profile.officePhotos);
      if (agencyPhotos.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          agencyPhotos: agencyPhotos.map((photo, index) => ({
            id: `photo-${index}`,
            name: photo.name,
            file: new File([], photo.name) // Create a dummy file for display
          }))
        }));
      }

      const regCertFiles = parseFilesSafely(profile.registrationCertificateFiles || profile.registrationCertificates);
      if (regCertFiles.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          registrationCertificateFiles: regCertFiles.map((file, index) => ({
            id: `reg-${index}`,
            name: file.name,
            file: new File([], file.name) // Create a dummy file for display
          }))
        }));
      }
    }
  }, [isEditMode, vendorData]);

  // When editing, set state/city dropdowns
  useEffect(() => {
    if (isEditMode && vendorData?.fullProfile) {
      const profile = vendorData.fullProfile;
      if (profile.state) {
        setSelectedState(profile.state);
        const found = stateCityList.find(s => s.state === profile.state);
        setAvailableCities(found ? found.cities : []);
      }
    }
  }, [isEditMode, vendorData]);

  // Map location logic
  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ lat: latitude, lng: longitude });
          setForm(prev => ({ ...prev, googleMapsLocation: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` }));
          setIsGettingLocation(false);
        },
        (error) => {
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setIsGettingLocation(false);
    }
  };

  // Validation logic for each step
  const validateStep = (stepIdx: number): boolean => {
    const section = stepSections[stepIdx];
    const errors: { [key: string]: string } = {};
    section.fields.forEach(fieldName => {
      const value = form[fieldName];
      if ([
        'agencyName', 'ownerName', 'phoneNumber', 'email', 'completeAddress', 'state', 'city', 'pincode'
      ].includes(fieldName) && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[fieldName] = 'This field is required.';
      }
      if (fieldName === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
        errors[fieldName] = 'Invalid email address.';
      }
      if (fieldName === 'phoneNumber' && value && !/^\d{10}$/.test(value)) {
        errors[fieldName] = 'Enter a valid 10-digit phone number.';
      }
      if ([
        'deliveryOperationalAreas', 'languageProficiency', 'otherServicesProvided'
      ].includes(fieldName) && (!value || !Array.isArray(value) || value.length === 0)) {
        errors[fieldName] = 'Add at least one value.';
      }
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const ref = fieldRefs.current[firstErrorField];
      if (ref && typeof ref.scrollIntoView === 'function') {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(s => (s < stepSections.length - 1 ? s + 1 : s));
    }
  };
  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const handleNextClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (validateStep(step)) {
      setStep(s => (s < stepSections.length - 1 ? s + 1 : s));
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(s => Math.max(s - 1, 0));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const input = e.target as HTMLInputElement;
      setForm(prev => ({ ...prev, [name]: input.checked }));
    } else if (name === 'panNumber' || name === 'gstNumber') {
      const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setForm(prev => ({ ...prev, [name]: sanitized }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (field: string, files: Array<{ id: string; name: string; file: File }>) => {
    setUploadedFiles(prev => ({ ...prev, [field]: files }));
  };

  const handleReset = (fullReset: boolean = true) => {
    if (fullReset) {
      if (isEditMode) {
        navigate(location.pathname.startsWith('/vendors') ? '/vendors' : '/sales/vendor-onboarding');
      } else {
        setForm({});
        setUploadedFiles({ licenseFiles: [], registrationCertificateFiles: [], agencyPhotos: [] });
        setCoordinates(null);
        setStep(0);
        setSubmittedData(null);
      }
    }
    setShowSuccess(false);
    setErrorMsg(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== stepSections.length - 1) return;
    if (!validateStep(step)) return;
    setIsSubmitting(true);
    setErrorMsg(null);
    // Build API payload
    const payload: any = {
      phoneNumber: form.phoneNumber || '',
      email: form.email || '',
      submitted_by: user?.id || '',
      ...(isEditMode && vendorId && { vendorId: vendorId }),
      ambulanceDetails: {
        agencyName: form.agencyName,
        gstNumber: form.gstNumber,
        panNumber: form.panNumber,
        ownerName: form.ownerName,
        registrationNumber: form.registrationNumber,
        address: form.completeAddress,
        landmark: form.nearbyLandmark,
        contactNumber: form.phoneNumber,
        email: form.email,
        website: form.website,
        numOfAmbulances: Number(form.numOfAmbulances) || undefined,
        driverKYC: !!form.driverKYC,
        driverTrained: !!form.driverTrained,
        ambulanceTypes: form.ambulanceTypes || [],
        gpsTrackingAvailable: !!form.gpsTrackingAvailable,
        ambulanceEquipment: form.ambulanceEquipment || [],
        trainingCertifications: (uploadedFiles.licenseFiles || []).map(f => ({ name: f.name, url: f.file.name })),
        languageProficiency: form.languageProficiency || [],
        operationalAreas: form.deliveryOperationalAreas || [],
        is24x7Available: !!form.is24x7Operational,
        distanceLimit: form.distanceLimitations ? Number(form.distanceLimitations) : undefined,
        isOnlinePaymentAvailable: !!form.acceptsOnlinePayment,
        officePhotos: (uploadedFiles.agencyPhotos || []).map(f => ({ name: f.name, url: f.file.name })),
        preciseLocation: form.googleMapsLocation,
        driverLicense: form.driverLicense,
        state: form.state,
        city: form.city,
        pinCode: form.pincode,
      }
    };
    try {
      let response;
      console.log('Edit mode:', isEditMode, 'VendorId:', vendorId, 'Payload:', payload);
      if (isEditMode && vendorId) {
        response = await updateAmbulanceVendor(vendorId, payload);
      } else {
        response = await onboardAmbulanceVendor(payload);
      }
      setIsSubmitting(false);
      setShowSuccess(true);
      setSubmittedData(form);
    } catch (error: any) {
      setIsSubmitting(false);
      setErrorMsg(error?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'onboard'} ambulance vendor.`);
    }
  };

  // Prevent form submit on Enter except on submit button
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLElement;
      if (
        target.tagName !== 'TEXTAREA' &&
        (
          (target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'submit') ||
          (target.tagName === 'BUTTON' && (target as HTMLButtonElement).type !== 'submit') ||
          (target.tagName !== 'INPUT' && target.tagName !== 'BUTTON')
        )
      ) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mt-8">
      <div className="mb-8 pt-8">
        <Stepper
          steps={stepSections.map((section, idx) => ({
            title: section.title,
            icon: idx < step || showSuccess ? <CheckCircle className="w-6 h-6" /> : undefined
          }))}
          activeStep={showSuccess ? stepSections.length : step}
          activeColor="#2563eb"
          completeColor="#22c55e"
          defaultColor="#e5e7eb"
          activeTitleColor="#2563eb"
          completeTitleColor="#22c55e"
          defaultTitleColor="#6b7280"
          size={44}
          circleFontSize={22}
          titleFontSize={14}
          completeBarColor="#22c55e"
          defaultBarColor="#e5e7eb"
        />
      </div>
      {showSuccess ? (
        <div className="flex flex-col items-center justify-center py-16">
          <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-green-600 mb-2">Ambulance Vendor {isEditMode ? 'Updated' : 'Added'} Successfully!</h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 w-full max-w-md">
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Agency Name:</span> {submittedData?.agencyName}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Owner Name:</span> {submittedData?.ownerName}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Email:</span> {submittedData?.email}</div>
          </div>
          <button
            onClick={() => handleReset(true)}
            className="mt-2 px-6 py-2 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all"
          >
            {isEditMode ? 'Back to Vendor List' : 'Add Another Ambulance Vendor'}
          </button>
        </div>
      ) : errorMsg ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Failed to {isEditMode ? 'Update' : 'Add'} Ambulance Vendor</h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 w-full max-w-md text-center">
            <div className="text-gray-700 dark:text-gray-200 mb-1">{errorMsg}</div>
          </div>
          <button
            onClick={() => handleReset(false)}
            className="mt-2 px-6 py-2 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all"
          >
            Try Again
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-6">
          {getFieldsForStep(step).map((field, idx, arr) => {
            // Group toggles in pairs
            if (["is24x7Operational","isAllDaysWorking","acceptsOnlinePayment","driverKYC","driverTrained","gpsTrackingAvailable"].includes(field.name)) {
              if (idx % 2 === 0 && ["is24x7Operational","isAllDaysWorking","acceptsOnlinePayment","driverKYC","driverTrained","gpsTrackingAvailable"].includes(arr[idx+1]?.name)) {
                return (
                  <div key={field.name} className="flex gap-8 mb-2">
                    <div className="flex-1">
                      <ToggleSwitch
                        label={field.label}
                        name={field.name}
                        checked={!!form[field.name]}
                        onChange={val => setForm(prev => ({ ...prev, [field.name]: val }))}
                      />
                    </div>
                    <div className="flex-1">
                      <ToggleSwitch
                        label={arr[idx+1].label}
                        name={arr[idx+1].name}
                        checked={!!form[arr[idx+1].name]}
                        onChange={val => setForm(prev => ({ ...prev, [arr[idx+1].name]: val }))}
                      />
                    </div>
                  </div>
                );
              } else if (idx % 2 === 1) {
                return null;
              } else {
                return (
                  <div key={field.name} className="mb-2">
                    <ToggleSwitch
                      label={field.label}
                      name={field.name}
                      checked={!!form[field.name]}
                      onChange={val => setForm(prev => ({ ...prev, [field.name]: val }))}
                    />
                  </div>
                );
              }
            }
            if (field.name === 'state') {
              return (
                <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                  {fieldErrors[field.name] && (
                    <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                  )}
                  <select
                    name="state"
                    value={selectedState || form.state || ''}
                    onChange={e => {
                      setSelectedState(e.target.value);
                      setForm(prev => ({ ...prev, state: e.target.value, city: '' }));
                      const found = stateCityList.find(s => s.state === e.target.value);
                      setAvailableCities(found ? found.cities : []);
                    }}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2"
                  >
                    <option value="">Select State</option>
                    {stateCityList.map(s => (
                      <option key={s.state} value={s.state}>{s.state}</option>
                    ))}
                  </select>
                </div>
              );
            }
            if (field.name === 'city') {
              return (
                <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                  {fieldErrors[field.name] && (
                    <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                  )}
                  <select
                    name="city"
                    value={form.city || ''}
                    onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2"
                    disabled={!selectedState}
                  >
                    <option value="">{selectedState ? 'Select City' : 'Select State First'}</option>
                    {availableCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{field.label}</label>
                {fieldErrors[field.name] && (
                  <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                )}
                {['deliveryOperationalAreas', 'languageProficiency', 'ambulanceTypes', 'ambulanceEquipment'].includes(field.name) ? (
                  <MultiValueInput
                    label={field.label}
                    name={field.name}
                    values={Array.isArray(form[field.name]) ? form[field.name] : (form[field.name] ? [form[field.name]] : [])}
                    onChange={vals => setForm(prev => ({ ...prev, [field.name]: vals }))}
                  />
                ) : field.name === 'googleMapsLocation' ? (
                  <div className="space-y-3">
                    <MapComponent
                      coordinates={coordinates}
                      onGetLocation={getCurrentLocation}
                      isGettingLocation={isGettingLocation}
                    />
                    <input
                      type="text"
                      name={field.name}
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2"
                      placeholder="Enter coordinates manually (e.g., 12.9716, 77.5946)"
                    />
                  </div>
                ) : field.name === 'completeAddress' ? (
                  <textarea
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    rows={3}
                  />
                ) : field.name === 'agencyPhotos' || field.name === 'licenseFiles' || field.name === 'registrationCertificateFiles' ? (
                  <FileUploadComponent
                    fieldName={field.name}
                    label={field.label}
                    files={uploadedFiles[field.name] || []}
                    onFilesChange={files => handleFileChange(field.name, files)}
                  />
                ) : field.name === 'numOfAmbulances' || field.name === 'distanceLimitations' ? (
                  <input
                    type="number"
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2 ${fieldErrors[field.name] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                ) : (
                  <input
                    type={field.name === 'email' ? 'email' : 'text'}
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-4 py-2 ${fieldErrors[field.name] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                )}
              </div>
            );
          })}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handleBackClick}
              disabled={step === 0}
              className="px-6 py-2 rounded-lg font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            {step < stepSections.length - 1 ? (
              <button
                type="button"
                onClick={handleNextClick}
                className="px-6 py-2 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="px-6 py-2 rounded-lg font-semibold bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white shadow-md transition-all flex items-center justify-center min-w-[180px]"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Ambulance Vendor' : 'Add Ambulance Vendor')}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default AmbulanceVendorForm; 